import { Injectable } from "@angular/core";
import { TranslocoService } from "@ngneat/transloco";
import { WidgetManagementService, WidgetSettingsGetDto } from "clearline-api";
import { WidgetType } from "clearline-common";
import { Observable } from "rxjs";
import { map, tap } from "rxjs/operators";
import { LocationService } from "./location.service";

interface FeatureItem {
  id: WidgetType;
  name: string;
  icon: string;
}

interface FeatureItemConfiguration {
  widgetType: WidgetType;
  translocoKey: string;
  icon: string;
}

const WIDGET_CONFIGURATION: FeatureItemConfiguration[] = [
  { widgetType: WidgetType.SmartPage, translocoKey: "features.smartPage.title", icon: "ri-settings-line" },
  { widgetType: WidgetType.IssueCoupon, translocoKey: "features.issueCoupon.title", icon: "ri-coupon-line" },
  { widgetType: WidgetType.ValidateCoupon, translocoKey: "features.validateCoupon.title", icon: "ri-qr-scan-line" },
  { widgetType: WidgetType.EnrollToLoyalty, translocoKey: "features.enrollLoyalty.title", icon: "ri-service-line" },
  { widgetType: WidgetType.IssuePointsOrStamps, translocoKey: "features.issuePoints.title", icon: "ri-hand-coin-line" },
  { widgetType: WidgetType.RequestReview, translocoKey: "features.requestReview.title", icon: "ri-chat-forward-line" },
  { widgetType: WidgetType.SetReminder, translocoKey: "features.setReminder.title", icon: "ri-alarm-line" },
  { widgetType: WidgetType.AddPromo, translocoKey: "features.addPromo.title", icon: "ri-file-list-3-line" },
  { widgetType: WidgetType.DownloadMobileApp, translocoKey: "features.downloadMobileApp.title", icon: "ri-smartphone-line" },
  {
    widgetType: WidgetType.DownloadPromotionMobileApp,
    translocoKey: "features.downloadPromotionMobileApp.title",
    icon: "ri-mobile-download-line"
  },
  { widgetType: WidgetType.ConnectSocialNetwork, translocoKey: "features.connectSocialMedia.title", icon: "ri-link" },
  { widgetType: WidgetType.OneTimePayment, translocoKey: "features.sendPaymentLink.title", icon: "ri-refund-2-line" },
  { widgetType: WidgetType.ScheduleAppointment, translocoKey: "features.scheduleAppointment.title", icon: "ri-calendar-todo-line" },
  { widgetType: WidgetType.AddToWaitlist, translocoKey: "features.waitlist.title", icon: "ri-user-add-line" },
  { widgetType: WidgetType.SendSurvey, translocoKey: "features.sendSurvey.title", icon: "ri-question-answer-line" },
  { widgetType: WidgetType.SendGiftCard, translocoKey: "features.sendGiftCard.title", icon: "ri-gift-2-line" },
  { widgetType: WidgetType.AnniversaryClub, translocoKey: "features.anniversaryClub.title", icon: "ri-cake-2-line" },
  { widgetType: WidgetType.AddToReferralProgram, translocoKey: "features.referralProgram.title", icon: "ri-medal-line" },
  { widgetType: WidgetType.SeeMenu, translocoKey: "features.seeMenu.title", icon: "ri-list-check-3" }
];

@Injectable({ providedIn: "root" })
export class FeaturesService {
  private widgets: WidgetSettingsGetDto[] = [];
  private featureItemConfiguration = new Map<WidgetType, FeatureItem>();

  constructor(
    private transloco: TranslocoService,
    private locationSvc: LocationService,
    private widgetManagementApiService: WidgetManagementService
  ) {
    this.initializeFeatureConfigurations();
  }

  getUserWidgets(locationId: number): Observable<WidgetSettingsGetDto[]> {
    return this.widgetManagementApiService.getUserWidgets(locationId);
  }

  getAvailableFeatures(): Observable<WidgetSettingsGetDto[]> {
    const locationId = this.locationSvc.getSelectedLocationId();

    return this.getUserWidgets(locationId).pipe(
      map((widgets) =>
        widgets.map((widget) => ({
          ...widget,
          disabled: !widget.configured
        }))
      ),
      tap((widgets) => {
        this.widgets = widgets;
      })
    );
  }

  getWidgetByFeatureId(featureId: WidgetType): WidgetSettingsGetDto {
    return this.widgets.find((w) => w.id === featureId);
  }

  private initializeFeatureConfigurations(): void {
    WIDGET_CONFIGURATION.forEach((feature) => {
      this.featureItemConfiguration.set(feature.widgetType, {
        id: feature.widgetType,
        name: this.transloco.translate(feature.translocoKey),
        icon: feature.icon
      });
    });
  }
}
